<template>
  <header class="top-bar">
    <div class="top-bar__controls--left w-1/3 sm:w-1/4">
      <div class="back text-white" @click="backMethod">
        <i class="el-icon-back text-20" />
        <span class="text-13 ml-2">商城</span>
      </div>
      <div class="save text-12 flex items-center">
        <label class="text-14">{{ name }}</label>
        <div v-if="saveLoading" class="saving ml-4 flex-shrink-0">
          <i class="el-icon-loading"></i>
          <span>正在保存...</span>
        </div>
        <div v-else-if="$store.state.site.isChange" class="saved ml-4 flex-shrink-0 is-change">
          <i class="el-icon-warning text-14" />
          <span class="ml-1">有未保存的修改</span>
        </div>
        <div v-else class="saved ml-4">
          <div class="w-[14px] h-[14px] flex items-center rounded-full bg-[#1FB565] justify-center">
            <icon-check />
          </div>
          <span class="ml-1">保存成功</span>
        </div>
      </div>
    </div>
    <div v-if="model" class="top-bar__controls--center">
      <button
        v-if="showAiEntry"
        class="ai-entry-btn"
        :class="{ 'is-active': aiPanelOpen }"
        type="button"
        :title="aiPanelOpen ? '关闭 AI 助手' : '打开 AI 助手'"
        @click="$emit('toggle-ai-panel')"
      >
        <svg class="ai-entry-icon" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1.5l1.5 4.4 4.4 1.5-4.4 1.5L8 13.4 6.5 8.9l-4.4-1.5 4.4-1.5L8 1.5z" />
        </svg>
        <span>AI 助手</span>
      </button>
      <im-select
        ref="select"
        v-model="SitePage.current"
        class="w-[120px]"
        :options="SitePage.list"
        value-key="id"
        label-key="name"
        size="small"
        is-page-select
        @change="onPageChange"
        @show-confirm-modal="onSelectShowConfirm"
      >
        <el-option slot="append" value="append">
          <div class="flex items-center pl-5" @click.stop="onPageChange('append')">
            <icon-add class="text-white" />
            <span class="ml-1">添加新页面</span>
          </div>
        </el-option>
      </im-select>
      <device-tab
        class="ml-4"
        :full-screen="fullScreen"
        @change="changeDevice"
        @toggle-full-screen="(val) => $emit('toggle-full-screen', val)"
      />
    </div>
    <div class="top-bar__controls--right flex items-center justify-end w-1/3 sm:w-1/4">
      <button
        class="publish-btn text-14 mr-4 flex-shrink-0"
        :class="{ 'is-disabled': saveLoading }"
        @click="handleSave"
      >
        <i v-if="saveLoading" class="el-icon-loading mr-2" />
        保 存
      </button>
      <step-control :site-model="model" @clear-select="() => $emit('clear-select')" />
      <qrcode-view :model="model" />
      <div
        class="preview-area flex-shrink-0 flex items-center justify-center mr-4 cursor-pointer text-textColor-3 hover:text-textColor-5"
        @click="previewMethod"
      >
        <div class="preview-btn flex items-center justify-center cursor-pointer flex-shrink-0">
          <div class="play-triangle ml-1" />
        </div>
        <span class="ml-2 text-14">预览</span>
      </div>
      <button class="publish-btn text-14 mr-2 flex-shrink-0" :disabled="!isLicense" @click="publishMethod">
        <i class="el-icon-upload2 mr-2 text-16"></i>
        发 布
      </button>
    </div>
    <save-confirm ref="saveConfirm" @back="onConfirmBack" />
    <publish-dialog ref="$publish" :loading="saveLoading" @publish="handlePublish" @save-publish="handleSavePublish" />
    <publish-confirm-dialog ref="$publishConfirm" @publish="handlePublish" />
  </header>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash-es'
import { Message } from 'element-ui'
import { useAssets } from '~/hooks/useAssets'
import { DefaultDevice, DeviceList } from '~/settings/deviceSetting'
import QrcodeView from '~/site/components/qrcodeView'
import iconAdd from '@/assets/svg/icon/iconUpload.svg'
import iconCheck from '@/assets/svg/icon/iconCheck.svg'
import ImSelect from '~/components/common/ImSelect'
import { postMessage } from '~/utils/event'
import { PostMessageEvent } from '~/enums/postMessageEvent'
import StepControl from '~/site/components/stepControl.vue'
import DeviceTab from '~/site/components/deviceTab.vue'
import SaveConfirm from '~/site/components/saveConfirm/index.vue'
import PublishDialog from '~/site/components/publishDialog/index.vue'
import PublishConfirmDialog from '~/site/components/publishDialog/confirm.vue'
import { on, off } from '~/utils/dom'
import { isRelease } from '~/config'

export default {
  name: 'UiEditHeader',
  components: {
    PublishConfirmDialog,
    PublishDialog,
    DeviceTab,
    ImSelect,
    QrcodeView,
    iconCheck,
    iconAdd,
    StepControl,
    SaveConfirm,
  },
  props: {
    fullScreen: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '标题',
    },
    model: {
      type: Object,
      required: true,
      default() {
        return {}
      },
    },
    device: {
      type: String,
      default: DefaultDevice,
    },
    currentEditSite: {
      type: Object,
      default() {
        return {}
      },
    },
    aiPanelOpen: {
      type: Boolean,
      default: false,
    },
    aiPanelFloating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      changeDialogVisible: true,
      saveLoading: false,
      debounceSave: '',
      deviceList: DeviceList,
      loadingService: {},
      nextPageValue: null,
    }
  },
  computed: {
    ...mapState({
      domain: (state) => state.project.domain,
      merchantInfo: (state) => state.project.merchantInfo,
    }),
    isLicense() {
      return this.merchantInfo?.status || false
    },
    showAiEntry() {
      // 生产环境(release)不展示 AI 助手入口，测试/开发/预发布环境展示
      return !isRelease
    },
    langList() {
      const { siteLangList } = useAssets(this)
      return [...siteLangList] || []
    },
    siteValue() {
      return JSON.stringify(this.currentEditSite, (key, val) => {
        if (key.includes('__')) {
          return undefined
        }
        return val
      })
    },
  },
  watch: {
    siteValue: {
      deep: false,
      handler(val, oldVal) {
        const canSubmit = window.__site_builder_edit_init
        if (canSubmit && val !== oldVal) {
          this.$store.commit('site/SET_IS_CHANGE', true)
          off(window, 'beforeunload', this.beforeUnloadHandler)
          on(window, 'beforeunload', this.beforeUnloadHandler)
        }
      },
    },
  },
  created() {
    this.debounceSave = debounce(this.submit, 1000)
  },
  mounted() {
    window.addEventListener('popstate', function (evt) {}, false)
  },
  methods: {
    async handleSavePublish() {
      await this.submit()
      this.handlePublish()
      this.$refs.$publish.close()
    },
    beforeUnloadHandler(event) {
      event.preventDefault()
      event.returnValue = true
    },
    handlePublish() {
      this.$emit('publish')
    },
    onPageChange(value) {
      if (value === 'append') {
        this.$refs.select.blur()
      }
      this.$emit('page-change', value)
    },
    changeDevice(value) {
      this.$emit('device-change', value)
    },
    // submitSiteHome() {
    //   if (this.saveLoading) return
    //   this.saveLoading = true
    //   this.model.updateLocalSiteHomeGlobalMod()
    //   this.model.updateSiteHome().then(() => {
    //     this.savedMethod()
    //   }).catch(() => {
    //     this.savedErrorMethod()
    //   }).finally(() => {
    //     this.saveLoading = false
    //   })
    // },
    submit() {
      if (this.saveLoading) return
      this.saveLoading = true
      return new Promise((resolve, reject) => {
        this.model
          .submit()
          .then(() => {
            off(window, 'beforeunload', this.beforeUnloadHandler)
            this.$store.commit('site/SET_IS_CHANGE', false)
            this.savedMethod()
            resolve()
          })
          .catch((err) => {
            this.savedErrorMethod()
            Message.error(err)
            reject(err)
          })
          .finally(() => {
            this.saveLoading = false
          })
      })
    },
    savedErrorMethod() {
      postMessage({ type: PostMessageEvent.SAVED_ERROR })
    },
    savedMethod() {
      postMessage({ type: PostMessageEvent.SAVED })
    },
    handleSave() {
      this.submit()
    },
    toPreview() {
      const { params, query } = this.$route
      let path = `/preview/${params.sid}`
      if (query.page && query.page !== 'home') {
        path += `/${query.page}`
      }
      const route = this.$router.resolve({ path, params })
      window.open(route.href, '_blank')
    },
    async previewMethod() {
      if (this.$store.state.site.isChange) {
        await this.submit()
        this.toPreview()
      } else {
        this.toPreview()
      }
    },
    publishMethod() {
      if (this.$store.state.site.isChange) {
        this.$refs.$publish.init()
      } else {
        this.$refs.$publishConfirm.init()
      }
    },
    onConfirmBack() {
      if (this.nextPageValue) {
        this.$refs.saveConfirm.close()
        this.onPageChange(this.nextPageValue)
      } else {
        this.$emit('back')
      }
    },
    onSelectShowConfirm(value) {
      this.nextPageValue = value
      this.$refs.saveConfirm.init()
    },
    backMethod() {
      if (this.$store.state.site.isChange) {
        this.nextPageValue = null
        this.$refs.saveConfirm.init()
      } else {
        this.$emit('back')
      }
    },
    handleAddModules(modules) {
      if (!modules || !modules.length) return
      modules.forEach((item) => {
        if (item.model) {
          const widget = new item.model()
          this.model.addWidgetLast(widget)
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.top-bar {
  height: var(--header-height);
  display: flex;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  font-size: 14px;
  justify-content: space-between;
  position: relative;
  // 玻璃拟态背景
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(12px);
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0 24px;

  .top-bar__controls {
    &--left {
      display: flex;
      flex-direction: row;
      align-items: center;

      .back {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 16px;
        height: 36px;
        color: var(--text-light-color);
        cursor: pointer;
        border-radius: 8px;
        transition: all 0.2s ease;
        margin-right: 16px;

        &:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        i {
          font-size: 18px;
        }

        // 移除旧的分隔线
        &:after {
          display: none;
        }
      }

      .save {
        display: flex;
        align-items: center;
        padding: 0 12px;
        height: 36px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.06);
        min-width: 0; // 允许作为左侧 flex 项收缩，避免撑开中间/右侧空间

        label {
          color: #fff;
          font-weight: 500;
          min-width: 0;
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .saved {
          color: #00c970;
          padding: 0 8px;
          height: 22px;
          background-color: rgba(62, 200, 125, 0.15);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          margin-left: 12px;

          &.is-change {
            background-color: rgba(230, 162, 60, 0.15);
            color: #e6a23c;
          }
        }
      }
    }

    &--center {
      text-align: center;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;

      // 中间区域的组件样式优化可能需要深入组件内部，这里主要优化容器布局
      .ai-entry-btn {
        position: relative;
        display: flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 14px;
        margin-right: 8px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 6px;
        color: var(--text-light-color);
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;

        .ai-entry-icon {
          width: 14px;
          height: 14px;
          color: var(--primary-color);
          transition: color 0.2s ease;
        }

        &:hover {
          background: rgba(var(--primary-color-rgb), 0.12);
          border-color: rgba(var(--primary-color-rgb), 0.4);
          color: #fff;

          .ai-entry-icon {
            color: #fff;
          }
        }

        // AI 面板打开时的激活态：底部主题色指示条 + 图标微光
        &.is-active {
          background: transparent;
          border-color: rgba(var(--primary-color-rgb), 0.32);
          color: #fff;

          .ai-entry-icon {
            color: var(--primary-color);
            filter: drop-shadow(0 0 5px rgba(var(--primary-color-rgb), 0.55));
          }

          // 底部主题色指示短横线
          &::after {
            content: '';
            position: absolute;
            left: 50%;
            bottom: 3px;
            transform: translateX(-50%);
            width: 18px;
            height: 2px;
            border-radius: 2px;
            background: var(--primary-color);
            box-shadow: 0 0 6px rgba(var(--primary-color-rgb), 0.5);
          }
        }
      }
    }

    &--right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px; // 使用 gap 代替 margin

      .preview-area {
        height: 36px;
        padding: 0 16px;
        background-color: transparent;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        color: var(--text-light-color);

        &:hover {
          background-color: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.3);
          color: #fff;
          transform: translateY(-1px);
        }

        .preview-btn {
          width: 18px;
          height: 18px;
          background-color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;

          .play-triangle {
            width: 0;
            height: 0;
            border-top: 3px solid transparent;
            border-left: 6px solid #333; // 深色箭头
            border-bottom: 3px solid transparent;
            margin-left: 2px;
          }
        }
      }

      .publish-btn {
        height: 32px;
        padding: 0 16px;
        // 使用 CSS 变量和 color-mix (如果支持) 或回退到静态渐变，因为 Less 无法处理 var() 中的 lighten
        // 这里使用两个 CSS 变量来构建渐变，或者使用静态颜色
        background: linear-gradient(135deg, var(--primary-color) 0%, #5c6fff 100%);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 6px;
        font-weight: 500;
        font-size: 13px;
        letter-spacing: 0.5px;
        box-shadow: 0 4px 12px rgba(61, 70, 245, 0.3);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        border: none;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(61, 70, 245, 0.4);
          filter: brightness(1.1);
        }

        &:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(61, 70, 245, 0.2);
        }

        &.is-disabled {
          pointer-events: none;
          opacity: 0.6;
          box-shadow: none;
          background: #444;
        }

        &[disabled] {
          pointer-events: none;
          opacity: 0.5;
          background: #555;
          box-shadow: none;
        }
      }
    }
  }
}
</style>
<style lang="less">
.edit-drop-down-menu {
  background: #2a2936;
  border: none;
  padding: 8px;

  //top: 50px !important;
  &__item {
    color: #ffffff;
    line-height: 28px;
    padding: 0px 77px 0px 26px;
    font-size: 12px;

    &:hover {
      color: #ffffff !important;
      background: #2a2936 !important;
    }
  }

  .active {
    padding-left: 8px;
    color: #ffffff !important;
    background: #7b66f6 !important;
  }

  .popper__arrow {
    display: none;
  }
}
</style>
